/**
 * ONE PERIOD BUTTON (design 24, T15; the teammate: "two rows of 'press' buttons is not great" and
 * "let us click the dates and choose the range we need"; the owner, 2026-10-11: "a date picker with
 * predefined periods and a custom one, like all other apps' best practice").
 *
 * WHAT IT DRAWS: one button showing the period and its days - "Last 30 days · Sep 9 – Oct 8, 2026" -
 * that opens a menu: the set periods the screen hands in, the chosen one ticked, and "Custom range":
 * a calendar to click a first and a last day, the same two days typed for the keyboard, and "Apply".
 *
 * WHAT IT REFUSES, AND WHY:
 *   - NO CLOCK. The screen hands in `latest`, the last day that can be picked (the cabinet: the
 *     server's latest complete day). The calendar opens on the shown period's month, and no day
 *     after `latest` can be picked or typed; nothing here reads the browser's "today" (./days.ts).
 *   - NO RANGE LONGER THAN `longest` (the server reads at most 366 days): "Apply" waits, and the
 *     menu says "Up to 366 days".
 *   - IT COUNTS NOTHING. A set period's days come from the screen with it; the picker hands back
 *     what was chosen, and the screen reads it.
 *
 * Built on @mantine/dates' `DatePicker`, which this package already carries, so neither app adds a
 * dependency. Its day buttons are named "26 August 2031" by Mantine.
 *
 * ★ MONTHS ONLY (`mode="months"`, T15b): the same button and menu for a screen that reads one whole
 * month - the cabinet's audience, sealed month by month, whose months do not add up into a range. The
 * menu lists the screen's shortcuts ("Last month"), then its months; no calendar, no custom range.
 */
import { Button, Popover, TextInput, UnstyledButton } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { CalendarRange, Check, ChevronDown } from '../../icons';
import { useUiText } from '../../texts/ui-texts';
import type { UiTranslate } from '../../texts/ui-texts';
import { daysIn, formatRangeInEnglish, isDay, monthOf } from './days';
import type { PeriodRange } from './days';
import classes from './PeriodPicker.module.css';

/** A set period: its words, and the days the screen counted for it. */
export interface PeriodPreset<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly range: PeriodRange;
}

/** What the picker hands back for a custom range. */
export const CUSTOM_RANGE = 'custom';

export interface PeriodPickerProps<T extends string> {
  readonly mode?: 'days';
  /** What the button chooses: "Period". It names the button, and the set periods' group. */
  readonly label: string;
  /** The set periods, in the menu's order. */
  readonly presets: readonly PeriodPreset<T>[];
  /** The chosen set period, or `CUSTOM_RANGE`. */
  readonly value: T | typeof CUSTOM_RANGE;
  /** The days shown on the button: the chosen period's. */
  readonly range: PeriodRange;
  /** The last day that can be picked. */
  readonly latest: string;
  /** The longest custom range, in days, both ends counted. */
  readonly longest: number;
  readonly onChange: (value: T | typeof CUSTOM_RANGE, range: PeriodRange) => void;
  /** A range in words; American English by default. A screen passes its own language's. */
  readonly formatRange?: (range: PeriodRange) => string;
}

/** A month to pick: its words in the menu, and the month's own name for the button. */
export interface MonthChoice<T extends string> {
  readonly value: T;
  /** "Last month", "August 2031 (still open)". */
  readonly label: string;
  /** "July 2031": the button shows it beside a shortcut, or alone for a month; else the label. */
  readonly month?: string;
}

export interface MonthPeriodPickerProps<T extends string> {
  readonly mode: 'months';
  /** What the button chooses: "Month". */
  readonly label: string;
  /** Shortcuts, listed first: "Last month". */
  readonly presets: readonly MonthChoice<T>[];
  /** The months, in the menu's order. */
  readonly months: readonly MonthChoice<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}

/** The period button: days (set periods and a custom range), or `mode="months"`. */
export function PeriodPicker<T extends string>(
  props: PeriodPickerProps<T> | MonthPeriodPickerProps<T>,
) {
  return props.mode === 'months' ? <MonthsPicker {...props} /> : <DaysPicker {...props} />;
}

function calendarLabels(t: UiTranslate) {
  return {
    previousMonth: t('calendar.previousMonth'),
    nextMonth: t('calendar.nextMonth'),
    previousYear: t('calendar.previousYear'),
    nextYear: t('calendar.nextYear'),
    previousDecade: t('calendar.previousDecade'),
    nextDecade: t('calendar.nextDecade'),
    monthLevelControl: t('calendar.chooseMonth'),
    yearLevelControl: t('calendar.chooseYear'),
  };
}

function DaysPicker<T extends string>({
  label,
  presets,
  value,
  range,
  latest,
  longest,
  onChange,
  formatRange = formatRangeInEnglish,
}: PeriodPickerProps<T>) {
  const t = useUiText();
  const [opened, setOpened] = useState(false);
  const customId = useId();
  // Two months side by side where they fit; one on a phone.
  const wide = useMediaQuery('(min-width: 40em)', false);
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const [startText, setStartText] = useState('');
  const [endText, setEndText] = useState('');
  const [month, setMonth] = useState(monthOf(latest));

  const preset = value === CUSTOM_RANGE ? undefined : presets.find((one) => one.value === value);
  const name = preset?.label ?? t('periodPicker.customRange');

  /** The month the calendar shows first, so that `day`'s month is the last one on screen. */
  const showing = (day: string): string => monthOf(day > latest ? latest : day, wide ? -1 : 0);

  const open = () => {
    // Each time it opens: the shown period, on its own months.
    setStart(range.from);
    setEnd(range.to);
    setStartText(range.from);
    setEndText(range.to);
    setMonth(showing(range.to));
    setOpened(true);
  };

  const picked = (first: string | null, second: string | null) => {
    setStart(first);
    setEnd(second);
    setStartText(first ?? '');
    setEndText(second ?? '');
  };

  const typed = (which: 'start' | 'end', text: string) => {
    const day = isDay(text) && text <= latest ? text : null;
    if (which === 'start') {
      setStartText(text);
      setStart(day);
    } else {
      setEndText(text);
      setEnd(day);
    }
    if (day !== null) {
      setMonth(showing(day));
    }
  };

  const draft =
    start === null || end === null
      ? undefined
      : start <= end
        ? { from: start, to: end }
        : { from: end, to: start };
  const tooLong = draft !== undefined && daysIn(draft) > longest;
  const notADay = (text: string) => text.length >= 10 && !(isDay(text) && text <= latest);
  const lastDay = t('periodPicker.typeADay', {
    lastDay: formatRange({ from: latest, to: latest }),
    example: latest,
  });

  const choose = (chosen: T | typeof CUSTOM_RANGE, days: PeriodRange) => {
    setOpened(false);
    onChange(chosen, days);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      radius="lg"
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <PeriodButton
          label={label}
          name={name}
          detail={formatRange(range)}
          opened={opened}
          onClick={() => {
            if (opened) {
              setOpened(false);
            } else {
              open();
            }
          }}
        />
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <div className={classes.menu}>
          <div role="group" aria-label={label} className={classes.choices}>
            {presets.map((one) => (
              <Choice
                key={one.value}
                label={one.label}
                pressed={one.value === value}
                onClick={() => {
                  choose(one.value, one.range);
                }}
              />
            ))}
          </div>
          <div role="group" aria-labelledby={customId} className={classes.custom}>
            <p id={customId} className={classes.customTitle}>
              {t('periodPicker.customRange')}
            </p>
            <DatePicker
              type="range"
              allowSingleDateInRange
              value={draft === undefined ? [start, end] : [draft.from, draft.to]}
              onChange={([first, second]) => {
                picked(first, second);
              }}
              date={month}
              onDateChange={setMonth}
              maxDate={latest}
              numberOfColumns={wide ? 2 : 1}
              ariaLabels={calendarLabels(t)}
            />
            <div className={classes.typed}>
              <TextInput
                size="xs"
                label={t('periodPicker.startDate')}
                placeholder="YYYY-MM-DD"
                value={startText}
                error={notADay(startText) ? lastDay : undefined}
                onChange={(event) => {
                  typed('start', event.currentTarget.value);
                }}
              />
              <TextInput
                size="xs"
                label={t('periodPicker.endDate')}
                placeholder="YYYY-MM-DD"
                value={endText}
                error={notADay(endText) ? lastDay : undefined}
                onChange={(event) => {
                  typed('end', event.currentTarget.value);
                }}
              />
            </div>
            <div className={classes.applyRow}>
              <span className={classes.limit} data-over={tooLong ? 'true' : undefined}>
                {t('periodPicker.upToDays', { count: longest })}
              </span>
              <Button
                size="xs"
                disabled={draft === undefined || tooLong}
                onClick={() => {
                  if (draft !== undefined && !tooLong) {
                    choose(CUSTOM_RANGE, draft);
                  }
                }}
              >
                {t('periodPicker.apply')}
              </Button>
            </div>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/* ------------------------------------------------------------------------------ months only */

function MonthsPicker<T extends string>({
  label,
  presets,
  months,
  value,
  onChange,
}: MonthPeriodPickerProps<T>) {
  const [opened, setOpened] = useState(false);
  const shortcut = presets.find((one) => one.value === value);
  const named = months.find((one) => one.value === value);

  const choose = (chosen: T) => {
    setOpened(false);
    onChange(chosen);
  };
  const choice = (one: MonthChoice<T>) => (
    <Choice
      key={one.value}
      label={one.label}
      pressed={one.value === value}
      onClick={() => {
        choose(one.value);
      }}
    />
  );

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      radius="lg"
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <PeriodButton
          label={label}
          name={shortcut?.label ?? named?.month ?? named?.label ?? ''}
          detail={shortcut?.month}
          opened={opened}
          onClick={() => {
            setOpened((open) => !open);
          }}
        />
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <div role="group" aria-label={label} className={classes.choices}>
          {presets.map(choice)}
          {presets.length === 0 ? null : <div aria-hidden="true" className={classes.rule} />}
          <div className={classes.months}>{months.map(choice)}</div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/* --------------------------------------------------------------------------------- the parts */

interface PeriodButtonProps {
  readonly label: string;
  /** The period's name: "Last 30 days". */
  readonly name: string;
  /** Its days, or the month it is: "Sep 9 – Oct 8, 2026". */
  readonly detail?: string | undefined;
  readonly opened: boolean;
  readonly onClick: () => void;
}

/**
 * The button: a calendar, the period's name, its days in gold, a chevron. Its name for a screen
 * reader starts with what it chooses and holds every word on it: "Period: Last 30 days · …".
 */
function PeriodButton({ label, name, detail, opened, onClick, ...popover }: PeriodButtonProps) {
  const shown = detail === undefined ? name : `${name} · ${detail}`;
  return (
    <UnstyledButton
      {...popover}
      className={classes.trigger}
      aria-label={`${label}: ${shown}`}
      data-opened={opened ? 'true' : undefined}
      onClick={onClick}
    >
      <CalendarRange size={14} aria-hidden className={classes.icon} />
      <span>{name}</span>
      {detail === undefined ? null : (
        <>
          <span className={classes.dot}> · </span>
          <span className={classes.detail}>{detail}</span>
        </>
      )}
      <ChevronDown size={14} aria-hidden className={classes.icon} />
    </UnstyledButton>
  );
}

function Choice({
  label,
  pressed,
  onClick,
}: {
  readonly label: ReactNode;
  readonly pressed: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button type="button" className={classes.choice} aria-pressed={pressed} onClick={onClick}>
      <span>{label}</span>
      {pressed ? <Check size={14} aria-hidden className={classes.tick} /> : null}
    </button>
  );
}
